/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {toggleThemeStyle} from '../theme/helpers';
import {
  dlsAlt,
  dlsColor,
  dlsDarkColor,
  dlsShadow,
  dlsTypography,
  dlsZIndex,
  isDarkPalette
} from '../theme/theme';
import {DLSTheme} from '../types/types';

const phoneFooter = {isIonSuite: false};

/**
 * Opts the app into the iOn Suite's phone editor footer (Cancel right next to Done on
 * the right). Editors in this package are shared with other apps, which keep their own
 * footer, so the Suite calls this once at start-up ...
 */
export const enableIonSuitePhoneFooter = (): void => {
  phoneFooter.isIonSuite = true;
};

/**
 * Phone footer alignment shared by the editor dialogs: right-aligned in the current theme
 * and in the iOn Suite, otherwise the original `space-between` ...
 */
export const getPhoneFooterJustify = (isCurrentTheme: boolean): string => (
  isCurrentTheme || phoneFooter.isIonSuite ? 'flex-end' : 'space-between'
);

export const styles = (theme: DLSTheme, isCurrentTheme = false) => ({
  formPaper : {
    backgroundColor                : `${dlsColor(isCurrentTheme ? 'white' : 'grey6')} !important`,
    maxWidth                       : '1068px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    borderRadius                   : isCurrentTheme ? 12 : 0,
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px',
      ...(isCurrentTheme ? {overflowY: 'auto'} : {})
    }
  },

  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)'
  },

  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },

  formWrapper : {
    padding                        : isCurrentTheme ? '0' : '0px 109px 0px',
    display                        : 'flex',
    flexDirection                  : 'column',
    gap                            : isCurrentTheme ? '16px' : 0,
    [theme.breakpoints.down('sm')] : {
      padding : '0px 0px 20px 0px'
    }
  },

  bulkFormWrapper : {
    padding                        : '0px 24px 0px',
    [theme.breakpoints.down('sm')] : {
      padding : '0px 0px 20px 0px'
    }
  },

  formWrapperCtc : {
    padding                        : '0px 85px 0px',
    [theme.breakpoints.down('sm')] : {
      padding : '0px 0px 20px 0px'
    }
  },

  formWrapperCtcAlt : {
    padding                        : '0px 20px 20px',
    minHeight                      : '600px',
    [theme.breakpoints.down('sm')] : {
      padding : '0px 0px 20px 0px'
    }
  },

  customFormWrapper : {
    padding                        : '0px 40px 0px',
    [theme.breakpoints.down('sm')] : {
      padding : '0px 0px 20px 0px'
    }
  },
  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor(isCurrentTheme ? 'pastelTeal' : 'grey4')}`
  },

  formTitle : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },

  formDialogContent : {
    overflow                       : 'unset',
    display                        : 'flex',
    flexDirection                  : 'column',
    paddingBottom                  : isCurrentTheme ? '20px !important' : 'revert-rule',
    gap                            : isCurrentTheme ? 12 : 0,
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },

  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },

  currentCloseButton : {
    position : 'absolute',
    right    : '19px'
  },

  form : {
    overflowY : 'auto',
    overflowX : 'hidden',
    ...(isCurrentTheme ? {paddingBottom: theme.spacing(2)} : {})
  },

  overrides : {
    marginTop : '32px'
  },

  overridesTitle : {
    ...dlsAlt('maps', 'layer', 'listHeader'),
    color : dlsColor('grey1'),
    width : '100%'
  },
  
  overridesReportDetails : {
    ...dlsAlt('maps', 'layer', 'listHeader'),
    color      : dlsColor('grey1'),
    fontWeight : 'normal',
    width      : '30%'
  },

  overridesReportTitle : {
    ...dlsAlt('maps', 'layer', 'listHeader'),
    color      : dlsColor('grey1'),
    width      : '100%',
    fontWeight : 'normal'
  },
  
  overridesSwitches : {
    padding : '20px 5px'
  },

  primaryRecipientsLabel : {
    textAlign   : 'right',
    marginRight : '20px',
    fontSize    : '14px',
    color       : dlsColor('black')
  },
  primaryRecipientsEmail : {
    marginBottom : '16px'
  },
  emailSubjectLabel : {
    marginTop : '12px'
  },
  separateEmail : {
    fontSize      : '12px',
    textTransform : 'lowercase',
    fontWeight    : 'normal',
    letterSpacing : '0'
  },

  minusIcon : {
    marginBottom : '14px'
  },

  formActions : {
    ...(isCurrentTheme ? {
      boxShadow : dlsShadow(5.3),
      zIndex    : 1
    } : {
      borderTop : `solid 1px ${dlsColor('grey4')}`
    }),
    [theme.breakpoints.down('sm')] : {
      justifyContent : getPhoneFooterJustify(isCurrentTheme),
      padding        : '8px 16px 8px 40px'
    }
  },

  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    paddingLeft : 16,
    ...(isCurrentTheme ? {
      fontSize      : '20px',
      color         : dlsColor('grey2'),
      textTransform : 'capitalize',
      fontFamily    : 'Roboto'
    } : {})
  },

  primaryButton : {
    width       : isCurrentTheme ? '68px' : '191px',
    marginRight : '28px',
    ...(isCurrentTheme ? {
      '& .MuiButton-root' : {
        fontWeight : '500 !important',
        height     : '29px',
        fontFamily : 'Inter',
        fontSize   : '14px'
      }
    } : {}),
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  },
  loadingPlaceholder : {
    alignItems                     : 'center',
    color                          : dlsColor('grey3'),
    display                        : 'flex',
    height                         : '100%',
    marginLeft                     : '15px',
    paddingTop                     : '15px',
    [theme.breakpoints.down('sm')] : {
      marginLeft : '0px'
    }
  },

  endAdornment : {
    margin     : '0 10px',
    whiteSpace : 'nowrap'
  },

  leftPadInput : {
    paddingLeft : '11px',
    color       : dlsColor('grey1')
  },

  upperCase : {
    textTransform : 'uppercase'
  },

  revertCase : {
    textTransform : 'none ! important'
  },

  fieldClass : {
    color     : dlsColor('grey2'),
    marginTop : '30px',
    fontSize  : '14px'
  },

  tableHeader : {
    fontSize : '16px !important'
  },

  switchWrapper : {
    display    : 'flex',
    alignItems : 'center',
    height     : '100%',
    paddingTop : '12px'
  },

  infoLabel : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey3'),
    letterSpacing : 'normal'
  },

  m2mBadgeSpacing : {
    marginLeft : '12px'
  },

  m2mRoot : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : '4px'
  },

  m2mSwitchWrapper : {
    display    : 'flex',
    alignItems : 'center',
    height     : '100%'
  },

  formWrapperFilter : {
    padding                        : '0px 30px 0px',
    [theme.breakpoints.down('sm')] : {
      padding : '0px 0px 20px 0px'
    }
  },

  dateHeader : {
    fontSize : '14px !important',
    color    : dlsDarkColor(isDarkPalette(theme), 'grey3', '#828282')
  },

  dateError : {
    fontSize : '14px !important',
    color    : dlsColor('caRed')
  },

  actionsTile : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : dlsColor('white'),
    padding         : 10,
    alignItems      : 'center'
  },

  reportActionsTile : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : dlsColor('white'),
    padding         : '0px 10px',
    alignItems      : 'center'
  },

  reportDuaration : {
    width      : '50%',
    fontFamily : 'Roboto',
    fontSize   : '14px'
  },

  reportError : {
    fontSize    : '12px',
    textAlign   : 'left',
    fontFamily  : 'Roboto',
    fontWeight  : '400',
    paddingLeft : '29px !important',
    color       : dlsColor('caRed'),
    lineHeight  : '0px'
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },
  datePickerItem : {
    zIndex : dlsZIndex('dateRangePicker')
  },
  modalHeadertext : isCurrentTheme ? {
    color         : dlsColor('grey2'),
    fontSize      : '20px',
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    lineHeight    : '24px',
    letterSpacing : '1px'
  } : {
    color      : dlsColor('grey1'),
    fontFamily : 'Neo Sans Std',
    fontSize   : '16px',
    fontWeight : '500',
    lineHeight : '24px',
    fontStyle  : 'normal'
  },
  modalSubtext : {
    color      : dlsColor('grey3'),
    fontFamily : 'Roboto',
    fontSize   : '14px',
    fontWeight : 'normal',
    lineHeight : '20px'
  },
  assetTypeChooserContainer : {
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'center',
    marginBottom   : '5px'
  },
  permissionTypeToggleButton : {
    position : 'absolute',
    left     : '12%'
  },
  chipContainer : {
    display   : 'flex',
    flexWrap  : 'wrap',
    maxHeight : '80px',
    overflowY : 'auto'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  body2 : {
    ...dlsTypography('body2'),
    fontSize   : '14px',
    lineHeight : '20px',
    margin     : '8px 0px'
  },
  contentRoot : {
    display       : 'flex',
    flexDirection : 'column',
    padding       : '0'
  },
  centered : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    height         : '100%'
  },
  customTab : {
    color                  : dlsColor('grey1'),
    letterSpacing          : '1px',
    '& .MuiTabs-indicator' : {
      backgroundColor : dlsColor('aaBlue'),
      height          : '4px'
    },
    '& .MuiTab-root' : {
      ...dlsAlt('typography', 'label1'),
      color        : dlsColor('grey1'),
      fontWeight   : 700,
      minWidth     : 'auto',
      padding      : '0px 10px',
      marginBottom : '-4px',
      transition   : 'color 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      '&:hover'    : {
        opacity : 1,
        color   : dlsColor('aaBlue')
      }
    },
    '& .MuiTab-root.Mui-selected' : {
      color     : dlsColor('aaBlue'),
      '&:hover' : {
        color : dlsColor('aaBlue')
      }
    },
    '& .MuiTab-wrapper' : {
      marginBottom : '-10px'
    }
  },
  checkboxTextFont : {
    fontSize : '14px'
  }
});

/**
 * Mobile-breakpoint `formDialogContent` override shared only by the vehicle
 * inspections editors (GroupEditor/ItemEditor/TemplateEditor) - spread this
 * into a caller's own `[theme.breakpoints.down('sm')]` block explicitly,
 * rather than folding it into `formDialogContent` above, since that style
 * is shared by every other editor dialog in the repo and most rely on
 * their own mobile overflow/maxHeight behavior.
 *
 * On current theme, `formPaper` itself scrolls on mobile (see `formPaper`
 * above) - keeping this content div ALSO scrollable would create two
 * nested/conflicting scroll regions.
 */
export const mobileScrollableFormContent = (isCurrentTheme: boolean) => ({
  overflow  : isCurrentTheme ? 'visible' : 'auto',
  maxHeight : 'max-content'
});

/**
 * Style overrides (pass as an EditorDialog's `styleOverrides`) that let ONE
 * tall dialog use more of the viewport, so its body doesn't need a vertical
 * scroll bar. The shared `paperScrollPaper` ceiling of `100% - 94px` is used by
 * every editor dialog in the repo, so it is left alone - only the dialogs that
 * pass this get the current theme's taller `100% - 32px`. The legacy value is
 * kept inside the toggle so it isn't replaced when the overrides are merged.
 *
 * The body also gets 8px of bottom padding in the current theme: a measured 8px
 * of descendant overflow (591px of content in a 583px box, with the paper far
 * below its ceiling) made the body scroll even though the dialog had room.
 */
export const tallDialogOverrides = () => ({
  paperScrollPaper : {
    maxHeight : toggleThemeStyle('calc(100% - 94px)', 'calc(100% - 32px)')
  },
  formDialogContent : {
    paddingBottom : toggleThemeStyle(undefined, '8px')
  }
});

export const getClasses = (classes) => ({
  paper            : classes.formPaper,
  paperScrollPaper : classes.paperScrollPaper,
  scrollPaper      : classes.scrollPaper
});

export default styles;
